import { Link } from "react-router-dom";
import { Music2, ArrowUpRight, Youtube, Disc3 } from "lucide-react";
import { useApi } from "@/hooks/useApi";
import { useSeo } from "@/hooks/useSeo";
import { useLang } from "@/i18n/LanguageContext";
import PageHero from "@/components/site/PageHero";
import { Reveal, SectionHead } from "@/components/site/Reveal";

const PLATFORMS = [
  { name: "Spotify", url: "https://open.spotify.com/intl-it/artist/7ngeIzyK6qFXTxEo9UXSg9?si=NCt0BhrfTCefzGImsHEhpQ", id: "spotify" },
  { name: "Apple Music", url: "https://music.apple.com/it/artist/echoes-from-above/1894971984", id: "apple-music" },
];

const EchoesPage = () => {
  const { t } = useLang();
  const { data: tracks } = useApi("/tracks");
  useSeo("echoes");

  return (
    <div className="bg-[#0E0D0C] text-[#F7F5F0]" data-testid="echoes-page">
      <PageHero
        testId="echoes-hero"
        image="/assets/editorial-sunset.webp"
        eyebrow={t("echoes.eyebrow")}
        lines={[t("echoes.heroA"), t("echoes.heroB")]}
        subtitle={t("echoes.heroSub")}
        height="h-[85vh]"
      />

      {/* DARE UN SUONO AI LUOGHI */}
      <section className="py-24 lg:py-36" data-testid="echoes-manifesto">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <div className="lg:sticky lg:top-32">
              <Reveal>
                <div className="flex items-center gap-4">
                  <span className="h-px w-12 bg-[#C5A059]" />
                  <span className="text-[11px] tracking-[0.3em] uppercase font-medium text-[#C5A059]">{t("echoes.projLabel")}</span>
                </div>
              </Reveal>
              <Reveal delay={0.2}>
                <p className="font-serif-display italic text-2xl lg:text-3xl text-[#D8B46C] mt-10 leading-snug">
                  {t("echoes.quote")}
                </p>
              </Reveal>
            </div>
          </div>
          <div className="lg:col-span-7 lg:col-start-6">
            <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl leading-[1.12]">
              <Reveal>{t("echoes.dareTitle")}</Reveal>
            </h2>
            <Reveal delay={0.15}>
              <p className="mt-8 text-base sm:text-lg leading-relaxed text-[#F7F5F0]/70">
                {t("echoes.dareBody")}
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* MUSICA ORIGINALE */}
      <section className="py-24 lg:py-32 border-t border-[#F7F5F0]/10" data-testid="echoes-catalogue">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10">
          <SectionHead dark eyebrow={t("echoes.catalogoEyebrow")} title={t("echoes.catalogoTitle")} />
          {tracks && tracks.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-14">
              {tracks.map((tr) => (
                <div key={tr.slug} className="border border-[#F7F5F0]/10 p-8" data-testid={`track-${tr.slug}`}>
                  <h3 className="font-serif-display text-2xl">{tr.title}</h3>
                  <p className="text-sm text-[#F7F5F0]/60 mt-3">{tr.description}</p>
                </div>
              ))}
            </div>
          ) : (
            <Reveal delay={0.15}>
              <div className="mt-14 border border-[#C5A059]/25 p-10 lg:p-14 flex flex-col lg:flex-row items-start lg:items-center gap-8" data-testid="catalogue-presentation">
                <span className="w-14 h-14 rounded-full border border-[#C5A059]/60 flex items-center justify-center text-[#C5A059] shrink-0">
                  <Disc3 size={22} strokeWidth={1.4} />
                </span>
                <p className="text-base sm:text-lg text-[#F7F5F0]/70 leading-relaxed max-w-3xl">
                  {t("echoes.catalogoBody")}
                </p>
              </div>
            </Reveal>
          )}
        </div>
      </section>

      {/* ASCOLTA E GUARDA */}
      <section className="py-24 lg:py-32 border-t border-[#F7F5F0]/10" data-testid="echoes-watch-listen">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <SectionHead dark eyebrow={t("echoes.ascoltaEyebrow")} title={<>{t("echoes.ascoltaTitleA")}<br /><span className="italic text-[#D8B46C]">{t("echoes.ascoltaTitleB")}</span></>} />
            <Reveal delay={0.15}>
              <p className="mt-8 text-[#F7F5F0]/65 leading-relaxed">
                {t("echoes.ascoltaBody")}
              </p>
            </Reveal>
            <Reveal delay={0.25}>
              <a
                href="https://www.youtube.com/channel/UCnVYIt5TUHeqZGo2nvOsfpQ"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="echoes-youtube-link"
                className="mt-9 inline-flex items-center gap-3 border border-[#C5A059]/60 px-7 py-3.5 text-[11px] tracking-[0.25em] uppercase hover:bg-[#C5A059] hover:text-[#121212] transition-colors duration-500"
              >
                <Youtube size={14} /> {t("echoes.filmsCta")}
              </a>
            </Reveal>
          </div>
          <Reveal delay={0.2}>
            <div className="relative">
              <img src="/assets/editorial-village.webp" alt={t("echoes.imgAlt")} loading="lazy" className="w-full object-cover opacity-90" style={{ aspectRatio: "4/3.4" }} />
              <div className="absolute -bottom-4 -left-4 w-full h-full border border-[#C5A059]/30 pointer-events-none" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* PIATTAFORME */}
      <section className="py-24 lg:py-28 border-t border-[#F7F5F0]/10" data-testid="echoes-platforms">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10 text-center">
          <Reveal>
            <Music2 size={22} strokeWidth={1.4} className="text-[#C5A059] mx-auto" />
            <h2 className="font-serif-display text-3xl lg:text-4xl mt-6">{t("echoes.platformsTitle")}</h2>
            <p className="text-sm text-[#F7F5F0]/55 mt-4 max-w-md mx-auto">{t("echoes.platformsSub")}</p>
          </Reveal>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            {PLATFORMS.map((p) => (
              <a
                key={p.id}
                href={p.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`platform-link-${p.id}`}
                className="inline-flex items-center gap-2.5 border border-[#C5A059]/40 px-8 py-4 text-[11px] tracking-[0.25em] uppercase hover:bg-[#C5A059] hover:text-[#121212] transition-colors duration-500"
              >
                {p.name} <ArrowUpRight size={13} />
              </a>
            ))}
          </div>
          <Reveal delay={0.2}>
            <Link to="/" className="mt-14 inline-block text-[11px] tracking-[0.3em] uppercase text-[#F7F5F0]/50 hover:text-[#C5A059] transition-colors editorial-link" data-testid="echoes-back-home">
              {t("echoes.backHome")}
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default EchoesPage;
